/**
 * TypingIndicator — three-dot "assistant is thinking" animation
 * (STORY-002-009 Implementation-logic step 8 / prototype `TypingDots`).
 *
 * Shown during the intent → retrieval window before the first token streams.
 * The prototype animates three dots with a staggered fade (`gtypedot`); the
 * production version uses Framer Motion so the timing lives in one place and
 * respects `prefers-reduced-motion` via Framer's reduced-motion handling.
 *
 * No layout/size animations (only opacity + a small translate) to keep FPS
 * smooth (story risk note).
 */
import { motion } from "framer-motion";
import { useTranslation } from "react-i18next";
import type { ReactElement } from "react";

import { useAssistantDesign } from "@/features/chat/useDesignFrame";
import "@/features/chat/assistant.css";

const DOT_DELAYS = [0, 0.15, 0.3] as const;

/** Spec §4.3: three 7px `--gd-neutral` dots at 0 / 0.2s / 0.4s. */
const DESIGN_DOT_DELAYS = ["0s", "0.2s", "0.4s"] as const;

export function TypingIndicator(): ReactElement {
  const { t } = useTranslation("chat");
  const design = useAssistantDesign();

  if (design) {
    /*
     * CSS rather than Framer here, because the design's reduced-motion
     * behaviour is a PINNED RESTING FRAME, not a removal (spec §6): the dots
     * are a persistent element saying "the assistant is answering", so
     * stopping the animation must not stop the message. `.gd-dot` in
     * `assistant.css` carries both the keyframes and that pin.
     */
    return (
      <span
        className="inline-flex items-center gap-[5px]"
        role="status"
        aria-live="polite"
        aria-label={t("routing.label")}
        data-testid="typing-indicator"
      >
        {DESIGN_DOT_DELAYS.map((delay) => (
          <span
            key={delay}
            aria-hidden="true"
            className="gd-dot size-[7px] rounded-full"
            style={{
              backgroundColor: "var(--gd-neutral)",
              animationDelay: delay,
            }}
          />
        ))}
      </span>
    );
  }

  return (
    <span
      className="inline-flex items-center gap-1"
      role="status"
      aria-live="polite"
      aria-label={t("routing.label")}
      data-testid="typing-indicator"
    >
      {DOT_DELAYS.map((delay, i) => (
        <motion.span
          key={i}
          className="size-1.5 rounded-full bg-muted-foreground"
          aria-hidden="true"
          initial={{ opacity: 0.25, y: 0 }}
          animate={{ opacity: [0.25, 1, 0.25], y: [0, -2, 0] }}
          transition={{
            duration: 1.2,
            delay,
            repeat: Infinity,
            ease: "easeInOut",
          }}
        />
      ))}
    </span>
  );
}

export default TypingIndicator;
